import React, { useEffect, useRef, useState } from "react";
import { decryptData, encryptData } from "../../utils/crypto";
import {
  CameraFill,
  ChevronLeft,
  Eye,
  EyeSlash,
  PlusLg,
  Shop,
  Trash,
  X,
} from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  createStore,
  getStoreDetails,
  updateStore,
} from "../../services/storeServices";

import Utils from "../../utils";
import {
  getCityList,
  getCountryList,
  getStateList,
} from "../../services/commonServices";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { customStyles } from "../../utils/function";
import { notify } from "../../components/Common/Alerts";
import { useConfig } from "../../components/Context/ConfigContext";
import { useSelector } from "react-redux";

const AddStore = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const { configData } = useConfig();
  let auth = useSelector((x) => x.auth.value);


  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark",
  );
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    gst_number: "",
    pan_number: "",
    address: "",
    landmark: "",
    city_id: "",
    country_id: "",
    state_id: "",
    pincode: "",
    latitude: "",
    longitude: "",
    business_type: "",
    status: 1,
    uuid: "",
    online_order: 0,
    order_start_time: "",
    order_end_time: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    mobile: "",
    gst_number: "",
    pan_number: "",
    address: "",
    city_id: "",
    country_id: "",
    state_id: "",
    pincode: "",
    business_type: "",
    landmark: "",
  });

  const [btnLoading, setBtnLoading] = useState(false);
  const [storeDetails, setStoreDetails] = useState("");
  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);

  const businessOptions = configData?.business_type || [];

  const selectedOption =
    businessOptions.find((opt) => opt.value === formData.business_type) || null;

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setFile(null);
    setPreview(null);
  };

  const resolveLocationFromAllFields = () => {
    const { address, landmark, pincode, city_id, state_id, country_id } =
      formData;

    const cityLabel =
      cityList.find((c) => String(c.value) === String(city_id))?.label || "";
    const stateLabel =
      stateList.find((s) => String(s.value) === String(state_id))?.label || "";
    const countryLabel =
      countryList.find((c) => String(c.value) === String(country_id))?.label ||
      "";

    const query = [
      address,
      landmark,
      pincode,
      cityLabel,
      stateLabel,
      countryLabel,
    ]
      .filter(Boolean)
      .join(", ");

    if (!query.trim()) return;

    geocodeAndMoveMap(query);
  };

  useEffect(() => {
    if (!window.google) return;

    const hasSavedLocation = formData?.latitude && formData?.longitude;

    const initMap = (lat, lng) => {
      const mapInstance = new window.google.maps.Map(
        document.getElementById("map"),
        {
          center: { lat, lng },
          zoom: 15,
        },
      );

      const markerInstance = new window.google.maps.Marker({
        position: { lat, lng },
        map: mapInstance,
        draggable: true,
      });

      setMap(mapInstance);
      setMarker(markerInstance);

      markerInstance.addListener("dragend", () => {
        const pos = markerInstance.getPosition();

        const newLat = pos.lat();
        const newLng = pos.lng();

        setFormData((prev) => ({
          ...prev,
          latitude: newLat,
          longitude: newLng,
        }));

        getAddressFromLatLng(newLat, newLng);
      });
    };

    if (hasSavedLocation) {
      initMap(Number(formData.latitude), Number(formData.longitude));
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setFormData((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lng,
          }));

          initMap(lat, lng);
        },
        (error) => {
          console.log("GPS error:", error);

          initMap(23.0225, 72.5714);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      initMap(23.0225, 72.5714);
    }
  }, [formData?.latitude, formData?.longitude]);

  const getAddressFromLatLng = (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== "OK" || !results[0]) return;

      const result = results[0];

      let address = "";
      let cityName = "";
      let stateName = "";
      let countryName = "";
      let pincode = "";
      let landmark = "";

      result.address_components.forEach((component) => {
        const types = component.types;

        if (types.includes("postal_code")) {
          pincode = component.long_name;
        }

        if (
          types.includes("locality") ||
          types.includes("administrative_area_level_2")
        ) {
          cityName = component.long_name;
        }

        if (types.includes("administrative_area_level_1")) {
          stateName = component.long_name;
        }

        if (types.includes("country")) {
          countryName = component.long_name;
        }

        if (
          types.includes("sublocality_level_1") ||
          types.includes("sublocality_level_2") ||
          types.includes("neighborhood") ||
          types.includes("premise")
        ) {
          landmark = component.long_name;
        }
      });

      // clean address
      address = result.formatted_address.split(",").slice(0, 2).join(", ");

      // ✅ STEP 2: Convert NAME → ID from dropdown lists
      const countryObj = countryList.find((c) => c.label === countryName);

      const stateObj = stateList.find((s) => s.label === stateName);

      const cityObj = cityList.find((c) => c.label === cityName);

      // ✅ STEP 3: Set FINAL FORM DATA (IMPORTANT)
      setFormData((prev) => ({
        ...prev,

        // address fields
        address: address,
        landmark: landmark || cityName,
        pincode: pincode,

        // dropdown fields (IDs only)
        country_id: countryObj?.value ? String(countryObj.value) : "",
        state_id: stateObj?.value ? String(stateObj.value) : "",
        city_id: cityObj?.value ? String(cityObj.value) : "",

        // optional lat/lng
        latitude: lat,
        longitude: lng,
      }));
    });
  };

  const getLatLngFromAddress = (address) => {
    if (!window.google) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status !== "OK" || !results[0]) return;

      const location = results[0].geometry.location;

      const lat = location.lat();
      const lng = location.lng();

      setFormData((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
      }));

      if (map) map.setCenter({ lat, lng });
      if (marker) marker.setPosition({ lat, lng });
    });
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setDarkMode(localStorage.getItem("theme") === "dark");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(function () {
    getCountryList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const formatted = decrypted.data.map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setCountryList(formatted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    if (!formData.country_id) {
      setStateList([]);
      return;
    }

    getStateList({ country_id: formData.country_id })
      .then((response) => {
        const decrypted = decryptData(response.data.data);

        const formatted = decrypted.data.map((item) => ({
          value: String(item.id),
          label: item.name,
        }));

        setStateList(formatted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, [formData.country_id]);

  useEffect(() => {
    if (!formData.state_id) {
      setCityList([]);
      return;
    }

    getCityList({ state_id: formData.state_id })
      .then((response) => {
        const decrypted = decryptData(response.data.data);

        const formatted = decrypted.data.map((item) => ({
          value: String(item.id),
          label: item.name,
        }));

        setCityList(formatted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, [formData.state_id]);

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value) return t("nameIsRequired");
        return "";

      case "mobile":
        if (!value) return t("mobileIsRequired");
        const mobileRegex = /^\d{10}$/;
        if (!mobileRegex.test(value)) {
          return t("enterDigitMobileNumber");
        }
        return "";

      case "address":
        if (!value) return t("addressIsRequired");
        return "";

      case "landmark":
        if (!value) return t("landmarkIsRequired");
        return "";

      case "city_id":
        if (!value) return t("cityIsRequired");
        return "";

      case "country_id":
        if (!value) return t("countryIsRequired");
        return "";

      case "state_id":
        if (!value) return t("stateIsRequired");
        return "";

      case "pincode":
        if (!value) return t("pincodeIsRequired");
        return "";

      case "business_type":
        if (!value) return t("busniessTypeIsRequired");
        return "";

      case "order_start_time":
        if (formData?.online_order === 1 && !value) {
          return t("orderStartTimeIsRequired");
        }
        return "";

      case "order_end_time":
        if (formData?.online_order === 1 && !value) {
          return t("orderEndTimeIsRequired");
        }

        // Check if end time > start time
        if (
          formData?.online_order === 1 &&
          formData?.order_start_time &&
          value &&
          new Date(`1970-01-01T${value}:00`) <=
            new Date(`1970-01-01T${formData.order_start_time}:00`)
        ) {
          return t("endTimeGreaterStartTime");
        }
        return "";

      default:
        return "";
    }
  };

  const handleMobileValidation = () => {
    let errorMsg = "";

    const mobileRegex = /^\d{10}$/;

    if (!formData.mobile) {
      errorMsg = "Mobile is required";
    } else if (!mobileRegex.test(formData.mobile)) {
      errorMsg = "Enter a valid 10-digit mobile number";
    }

    setErrors((prev) => ({
      ...prev,
      mobile: errorMsg,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      const cleanedValue = value.replace(/\D/g, "").slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        mobile: cleanedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  useEffect(
    function () {
      if (uuid) {
        let data = {
          uuid: uuid,
          store_id: auth.active_store_id,
        };
        getStoreDetails(data)
          .then((response) => {
            const decrypted = decryptData(response.data.data);
            setStoreDetails(decrypted.data);
          })
          .catch((err) => {
            notify.error(Utils.getErrorMessage(err));
          });
      }
    },
    [navigate, uuid],
  );

  useEffect(
    function () {
      if (storeDetails) {
        setFormData({
          name: storeDetails?.name,
          mobile: storeDetails?.mobile,
          gst_number: storeDetails?.gst_number,
          pan_number: storeDetails?.pan_number,
          address: storeDetails?.address,
          landmark: storeDetails?.landmark,
          city_id: storeDetails?.city_id,
          country_id: storeDetails?.country_id,
          state_id: storeDetails?.state_id,
          pincode: storeDetails?.pincode,
          status: storeDetails?.status,
          latitude: storeDetails?.latitude,
          longitude: storeDetails?.longitude,
          business_type: storeDetails?.business_type,
          uuid: storeDetails?.uuid,
          order_start_time: storeDetails?.order_start_time,
          order_end_time: storeDetails?.order_end_time,
          online_order: storeDetails?.online_order,
        });
        setFile(storeDetails?.store_image);
        setPreview(storeDetails?.store_image);
      }
    },
    [storeDetails],
  );

  const formatTime = (time) => {
    if (!time) return null;
    return time.length === 5 ? `${time}:00` : time;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      name: validateField("name", formData.name),
      mobile: validateField("mobile", formData.mobile),
      address: validateField("address", formData.address),
      city_id: validateField("city_id", formData.city_id),
      country_id: validateField("country_id", formData.country_id),
      state_id: validateField("state_id", formData.state_id),
      pincode: validateField("pincode", formData.pincode),
      business_type: validateField("business_type", formData.business_type),
      landmark: validateField("landmark", formData?.landmark),
      ...(formData?.online_order === 1 && {
        order_start_time: validateField(
          "order_start_time",
          formData.order_start_time,
        ),
        order_end_time: validateField(
          "order_end_time",
          formData.order_end_time,
        ),
      }),
    };

    Utils.focusError();
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((err) => err);
    if (hasErrors) return;

    if (!newErrors.name) {
      setBtnLoading(true);

      const payload = {
        name: formData.name?.trim() || "",
        mobile: formData.mobile || "",
        business_type: formData.business_type || "",
        address: formData.address?.trim() || "",
        landmark: formData.landmark || "",
        state_id: formData.state_id || "",
        country_id: formData.country_id || "",
        city_id: formData.city_id || "",
        pincode: formData.pincode || "",
        latitude: formData.latitude || "0.00",
        longitude: formData.longitude || "0.00",
        status: formData.status,
        order_start_time: formData.order_start_time || "",
        order_end_time: formData.order_end_time || "",
        online_order: formData.online_order || 0,
        store_id: auth.active_store_id,
        ...(formData.uuid && { uuid: formData.uuid }),
      };
      let q = encryptData(payload);

      const formPayload = new FormData();

      if (file) {
        formPayload.append("files", file);
      }
      formPayload.append("q", q);
      const apiCall = formData?.uuid ? updateStore : createStore;

      try {
        const response = await apiCall(formPayload);
        const decrypted = decryptData(response.data.data);
        notify.success(decrypted.message);
        setFormData({
          name: "",
          mobile: "",
          gst_number: "",
          pan_number: "",
          address: "",
          landmark: "",
          city_id: "",
          country_id: "",
          state_id: "",
          pincode: "",
          latitude: "",
          longitude: "",
          business_type: "",
          status: 1,
          uuid: "",
          online_order: 0,
          order_start_time: "",
          order_end_time: "",
        });
        setErrors("");
        navigate("/store");
      } catch (err) {
        notify.error(Utils.getErrorMessage(err));
      } finally {
        setBtnLoading(false);
      }
    }
  };

  const getSelectedOption = (list, value) =>
    list.find((opt) => String(opt.value) === String(value)) || null;

  return (
    <div className="w-full px-4 md:px-6 pb-10">
      <div className="hidden md:flex flex items-center gap-4 md:gap-6 flex-shrink-0 px-2 mb-2">
        <button
          onClick={() => navigate(-1)}
          className="hidden group flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all active:scale-95 shadow-sm"
        >
          <ChevronLeft
            size={18}
            className="group-hover:-translate-x-1 transition-transform"
          />
          <span className="hidden ml-2 font-bold text-sm">{t("back")}</span>
        </button>

        <div className="flex-1 hidden md:block">
          <h2 className="text-xl md:text-2xl font-black text-slate-800 dark:text-white tracking-tight leading-tight">
            {uuid ? t("updateNewStore") : t("addNewStore")}{" "}
          </h2>
        </div>
      </div>

      <section className="w-full bg-white dark:bg-slate-900 rounded-[45px] border border-slate-100 p-6 md:p-10 lg:p-14 shadow-sm">
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-brand-orange/10 text-brand-orange rounded-2xl flex items-center justify-center">
              <Shop size={24} />
            </div>
            <h2 className="text-xl font-black text-slate-800">
              {t("storeDetails")}{" "}
            </h2>
          </div>
        </div>

        <div className="lg:col-span-3 flex flex-col items-center justify-start mb-10">
          <div
            className="relative group cursor-pointer"
            onClick={() => inputRef.current.click()}
          >
            <input
              type="file"
              ref={inputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="hidden"
            />

            <div className="w-36 h-36 bg-gray-50 rounded-full border-4 border-white shadow-xl flex items-center justify-center overflow-hidden transition-transform group-hover:scale-105">
              {preview ? (
                <img
                  src={preview}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <CameraFill size={32} className="text-gray-200" />
              )}
            </div>

            {!file && (
              <div className="absolute bottom-2 right-2 bg-brand-orange text-white p-2.5 rounded-full border-4 border-white shadow-lg">
                <PlusLg size={16} />
              </div>
            )}

            {file && (
              <div
                onClick={handleDelete}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full border-4 border-white shadow-lg"
              >
                <Trash size={14} />
              </div>
            )}
          </div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-6">
            {t("profilePicture")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
          {" "}
          <div className="flex flex-col gap-2">
            <label htmlFor="name" className="input-label">
              {t("storeName")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              placeholder={t("enterStoreName")}
              className={`input-field ${errors.name ? "input-error" : ""}`}
              value={formData?.name || ""}
              onChange={handleChange}
            />
            {errors.name && <span className="text-danger">{errors.name}</span>}
          </div>
          {/* Mobile Number */}
          <div className="flex flex-col gap-2">
            <label htmlFor="mobile" className="input-label">
              {t("mobileNumber")}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="Enter 10 digit number"
              className="w-full pl-4 pr-4 py-4 bg-white border border-gray-100 rounded-2xl outline-none focus:border-brand-orange focus:ring-4 focus:ring-brand-orange/10 transition-all font-medium shadow-sm text-black placeholder-gray-400"
              value={formData.mobile}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onBlur={handleMobileValidation}
              name="mobile"
              min={0}
            />
            {errors.mobile && (
              <span className="text-danger">{errors.mobile}</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="pincode" className="input-label">
              {t("pincode")}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              name="pincode"
              placeholder={t("enteryourPincode")}
              className={`input-field ${errors.pincode ? "input-error" : ""}`}
              value={formData?.pincode || ""}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 6);

                setFormData((prev) => ({
                  ...prev,
                  pincode: value,
                }));

                setErrors((prev) => ({
                  ...prev,
                  pincode: validateField("pincode", value, {
                    ...formData,
                    pincode: value,
                  }),
                }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
            {errors.pincode && (
              <span className="text-danger">{errors.pincode}</span>
            )}
          </div>
          <div className="flex flex-col gap-2 hidden">
            <label htmlFor="gst_number" className="input-label">
              {t("GstNumber")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="gst_number"
              placeholder={t("enterGstNumber")}
              className={`input-field ${errors.gst_number ? "input-error" : ""}`}
              value={formData?.gst_number || ""}
              onChange={handleChange}
            />
            {errors.gst_number && (
              <span className="text-danger">{errors.gst_number}</span>
            )}
          </div>
          <div className="flex flex-col gap-2 hidden">
            <label htmlFor="pan_number" className="input-label">
              {t("panNumber")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="pan_number"
              placeholder={t("enterPanNumber")}
              className="input-field"
              value={formData?.pan_number || ""}
              onChange={handleChange}
            />
            {errors.pan_number && (
              <span className="text-danger">{errors.pan_number}</span>
            )}
          </div>
          {/* Full Address (Span full width of the left column) */}
          <div className="flex flex-col gap-2 md:col-span-2">
            <label htmlFor="address" className="input-label">
              {t("address")}
              <span className="text-red-500">*</span>
            </label>

            <textarea
              name="address"
              placeholder={t("enterYourAddress")}
              rows={3}
              className={`input-field ${errors.address ? "input-error" : ""}`}
              value={formData?.address || ""}
              onBlur={(e) => getLatLngFromAddress(e.target.value)}
              onChange={handleChange}
            />
            {errors.address && (
              <span className="text-danger">{errors.address}</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="landmark" className="input-label">
              {t("landmark")}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="landmark"
              placeholder={t("enterYourLandmark")}
              className={`input-field ${errors.landmark ? "input-error" : ""}`}
              value={formData?.landmark || ""}
              onChange={handleChange}
            />
            {errors.landmark && (
              <span className="text-danger">{errors.landmark}</span>
            )}
          </div>
          {/* Country & State */}
          <div className="flex flex-col gap-2">
            <label htmlFor="country_id" className="input-label">
              {t("country")}
              <span className="text-red-500">*</span>
            </label>
            <Select
              options={countryList}
              value={getSelectedOption(countryList, formData.country_id)}
              onChange={(selected) => {
                setFormData((prev) => ({
                  ...prev,
                  country_id: String(selected?.value ?? ""),
                  state_id: "",
                  city_id: "",
                }));
                setStateList([]);
                setCityList([]);
              }}
              styles={customStyles(darkMode)}
              placeholder={t("selectCountry")}
              isClearable={true}
              isSearchable={true}
            />
            {errors.country_id && (
              <span className="text-danger">{errors.country_id}</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label className="input-label" htmlFor="state_id">
              {t("state")}
              <span className="text-red-500">*</span>
            </label>
            <Select
              options={stateList}
              value={getSelectedOption(stateList, formData.state_id)}
              onChange={(selected) => {
                setFormData((prev) => ({
                  ...prev,
                  state_id: String(selected?.value ?? ""),
                  city_id: "",
                }));
                setCityList([]);
              }}
              styles={customStyles(darkMode)}
              placeholder={t("selectState")}
              isClearable={true}
              isSearchable={true}
            />
            {errors.state_id && (
              <span className="text-danger">{errors.state_id}</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="city_id" className="input-label">
              {t("city")}
              <span className="text-red-500">*</span>
            </label>
            <Select
              options={cityList}
              value={getSelectedOption(cityList, formData.city_id)}
              onChange={(selected) =>
                setFormData((prev) => ({
                  ...prev,
                  city_id: String(selected?.value ?? ""),
                }))
              }
              styles={customStyles(darkMode)}
              placeholder={t("selectCity")}
              isClearable={true}
              isSearchable={true}
            />
            {errors.city_id && (
              <span className="text-danger">{errors.city_id}</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="business_type" className="input-label">
              {t("businessType")}
              <span className="text-red-500">*</span>
            </label>
            <Select
              options={businessOptions}
              value={selectedOption}
              onChange={(selected) =>
                setFormData((prev) => ({
                  ...prev,
                  business_type: selected?.value ?? "",
                }))
              }
              styles={customStyles(darkMode)}
              placeholder="Select business type"
            />
            {errors.business_type && (
              <span className="text-danger">{errors.business_type}</span>
            )}
          </div>
          <div className="flex flex-col gap 2">
            <label htmlFor="status" className="input-label">
              {t("storeStatus")}
            </label>
            <label className="relative inline-flex items-center cursor-pointer group mt-3">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={formData.status === 1}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: e.target.checked ? 1 : 0,
                  }))
                }
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:bg-orange-500 transition-colors duration-300"></div>

              <div className="absolute left-[3px] top-[3px] w-[18px] h-[18px] bg-white rounded-full transition-all duration-300 shadow-sm peer-checked:translate-x-5"></div>
            </label>
          </div>
          <div className="flex flex-col gap 2">
            <label htmlFor="online_order" className="input-label">
              {t("onlineOrder")}
            </label>

            <label className="relative inline-flex items-center cursor-pointer group mt-3">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={formData.online_order === 1}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    online_order: e.target.checked ? 1 : 0,
                  }))
                }
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:bg-orange-500 transition-colors duration-300"></div>

              <div className="absolute left-[3px] top-[3px] w-[18px] h-[18px] bg-white rounded-full transition-all duration-300 shadow-sm peer-checked:translate-x-5"></div>
            </label>
          </div>
          {formData?.online_order === 1 && (
            <>
              <div className="flex flex-col gap-2">
                <label htmlFor="order_start_time" className="input-label">
                  {t("orderStartTime")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="order_start_time"
                  id="order_start_time"
                  className={`input-field ${errors.order_start_time ? "input-error" : ""}`}
                  value={formData?.order_start_time || ""}
                  onChange={handleChange}
                />
                {errors.order_start_time && (
                  <span className="text-danger">{errors.order_start_time}</span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="orderEnd" className="input-label">
                  {t("orderEndTime")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="order_end_time"
                  id="order_end_time"
                  className={`input-field ${errors.order_end_time ? "input-error" : ""}`}
                  value={formData?.order_end_time || ""}
                  onChange={handleChange}
                />
                {errors.order_end_time && (
                  <span className="text-danger">{errors.order_end_time}</span>
                )}
              </div>
            </>
          )}
          <div className="h-[300px] rounded-[24px] overflow-hidden border-2 border-dashed border-slate-200">
            <div id="map" className="w-full h-full"></div>
          </div>
        </div>

        <div className="col-span-full flex items-end justify-end gap-4 mb-20 lg:mb-0 mt-4">
          {" "}
          <button
            className="btn-filter-close"
            onClick={() => {
              navigate("/store");
            }}
          >
            {t("back")}
          </button>
          <button
            type="button"
            form="category-form"
            className="btn-submit-orange flex items-center gap-2 mb-1"
            onClick={handleSubmit}
            disabled={btnLoading}
          >
            {btnLoading && (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            )}
            {btnLoading
              ? t("submitting")
              : formData.uuid
                ? t("update")
                : t("submit")}
          </button>
        </div>
      </section>
    </div>
  );
};

export default AddStore;
